import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "../../utils/axiosInstance";
import { FixedSizeList as VirtualList } from "react-window";
import StarIcon from "@mui/icons-material/Star";
import { TextSlicer } from "../../utils/textSlicerDots";
import { useMediaQuery, useTheme } from "@mui/material";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";
export default function AddRecipient({
  onRecipientsUpdated,
  members = [],
  device,
}) {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down("sm")); // true only on xs screens
  const [availableUsers, setAvailableUsers] = useState([]);
  const [stagedUsers, setStagedUsers] = useState([]);
  const [stagedForRemoval, setStagedForRemoval] = useState([]);
  const [availableSearchQuery, setAvailableSearchQuery] = useState("");
  const [selectedSearchQuery, setSelectedSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [addingUsers, setAddingUsers] = useState(new Set());
  const [removingUsers, setRemovingUsers] = useState(new Set());
  const [savingRecipients, setSavingRecipients] = useState(false);

  // Notification states
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Refs for measuring available space for VirtualList
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const [leftHeight, setLeftHeight] = useState(0);
  const [rightHeight, setRightHeight] = useState(0);
  const itemHeight = 78; // 62px item height + 8px margin bottom + 8px padding

  const filteredAvailableUsers = React.useMemo(() => {
    if (!availableSearchQuery) return availableUsers;
    return availableUsers.filter(
      (user) =>
        (user.name || "")
          .toLowerCase()
          .includes(availableSearchQuery.toLowerCase()) ||
        (user.email || "")
          .toLowerCase()
          .includes(availableSearchQuery.toLowerCase()) ||
        (user.phone || "")
          .toLowerCase()
          .includes(availableSearchQuery.toLowerCase())
    );
  }, [availableUsers, availableSearchQuery]);

  const allSelectedRecipients = React.useMemo(() => {
    const existingRecipients = members
      .filter(
        (member) => !stagedForRemoval.some((staged) => staged.id === member.id)
      )
      .map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        phone: member.phone,
        members: member.members,
      }));

    return [...stagedUsers, ...existingRecipients];
  }, [members, stagedUsers, stagedForRemoval]);

  const filteredSelectedRecipients = React.useMemo(() => {
    if (!selectedSearchQuery) return allSelectedRecipients;
    return allSelectedRecipients.filter(
      (user) =>
        (user.name || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase()) ||
        (user.email || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase()) ||
        (user.phone || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase())
    );
  }, [allSelectedRecipients, selectedSearchQuery]);

  useEffect(() => {
    // Add null check for device
    if (device && device.id) {
      fetchAvailableUsers();
    }
  }, [device?.id]); // Only depend on device.id, not the entire device object or members

  const fetchAvailableUsers = async () => {
    // Add safety check
    if (!device || !device.id) {
      console.error("Device is not available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const allUsersResponse = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/users/getUnLinkedUsers/${device.id}`
      );
      const allUsers = allUsersResponse.data || [];

      const currentRecipientIds = new Set(
        members
          .filter(
            (member) =>
              !stagedForRemoval.some(
                (stagedUser) => stagedUser.id === member.id
              )
          )
          .map((member) => member.id)
      );
      const stagedUserIds = new Set(stagedUsers.map((user) => user.id));

      const available = allUsers.filter(
        (user) =>
          !currentRecipientIds.has(user.id) && !stagedUserIds.has(user.id)
      );

      const availableWithStaged = [...available, ...stagedForRemoval];
      setAvailableUsers(availableWithStaged);
    } catch (error) {
      console.error("Error fetching users:", error);
      showNotification("Error fetching users", "error");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  const MoveAddRecipient = async (user) => {
    setAddingUsers((prev) => new Set([user.id, ...prev]));
    try {
      setAvailableUsers((prev) => prev.filter((u) => u.id !== user.id));
      setStagedUsers((prev) => [user, ...prev]);

      setAddingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    } catch (error) {
      console.error(`Error staging recipient ${user.id}:`, error);
      showNotification(`Error staging ${user.name}`, "error");
    }
  };

  const handleSaveChanges = async () => {
    if (stagedUsers.length === 0 && stagedForRemoval.length === 0) {
      showNotification("No changes to save", "warning");
      return;
    }

    // Add safety check
    if (!device || !device.id) {
      showNotification("Device information is not available", "error");
      return;
    }

    setSavingRecipients(true);
    try {
      if (stagedUsers.length > 0) {
        const userIds = stagedUsers.map((user) => user.id);

        await axios.post(
          `${process.env.REACT_APP_API_GATEAWAY}/recipients/createMany`,
          {
            userIds: userIds,
            deviceId: device.id,
            UI_Description: `${getUIDescription()}\n  "Add one or many recipients"`,
          }
        );
      }

      if (stagedForRemoval.length > 0) {
        const userIds = stagedForRemoval.map((user) => user.id);
        const body = { userIds: userIds, UI_Description: `${getUIDescription()}\n delete one or many recipients`, };

        await axios.delete(
          `${process.env.REACT_APP_API_GATEAWAY}/recipients/device/${device.id}`,
          {
            data: body,
          }
        );
      }

      const addedCount = stagedUsers.length;
      const removedCount = stagedForRemoval.length;
      let message = "";

      if (addedCount > 0 && removedCount > 0) {
        message = `${addedCount} user(s) added and ${removedCount} user(s) removed successfully`;
      } else if (addedCount > 0) {
        message = `${addedCount} user(s) added successfully`;
      } else if (removedCount > 0) {
        message = `${removedCount} user(s) removed successfully`;
      }

      showNotification(message, "success");

      setStagedUsers([]);
      setStagedForRemoval([]);

      if (onRecipientsUpdated) {
        onRecipientsUpdated();
      }
    } catch (error) {
      console.error(`Error saving changes:`, error);
      showNotification(`Error saving changes`, "error");

      setAvailableUsers((prev) =>
        prev.filter(
          (user) =>
            !stagedUsers.some((stagedUser) => stagedUser.id === user.id) &&
            !stagedForRemoval.some((stagedUser) => stagedUser.id === user.id)
        )
      );
      setStagedUsers([]);
      setStagedForRemoval([]);
    } finally {
      setSavingRecipients(false);
    }
  };

  const handleRemoveRecipient = async (user) => {
    const isStagedUser = stagedUsers.some(
      (stagedUser) => stagedUser.id === user.id
    );

    if (isStagedUser) {
      setStagedUsers((prev) => prev.filter((u) => u.id !== user.id));
      setAvailableUsers((prev) => [user, ...prev]);
      showNotification(`${user.name} removed from selection`, "success");
      return;
    }

    const memberToRemove = members.find((member) => member.id === user.id);
    if (!memberToRemove) return;

    setStagedForRemoval((prev) => [memberToRemove, ...prev]);
    setAvailableUsers((prev) => [memberToRemove, ...prev]);
    showNotification(`${memberToRemove.name} staged for removal`, "success");
  };

  const handleAddFromStaged = (user) => {
    const isStagedForRemoval = stagedForRemoval.some(
      (member) => member.id === user.id
    );

    if (isStagedForRemoval) {
      setStagedForRemoval((prev) =>
        prev.filter((member) => member.id !== user.id)
      );
      setAvailableUsers((prev) => prev.filter((u) => u.id !== user.id));
      showNotification(`${user.name} restored to recipients`, "success");
      return;
    }

    MoveAddRecipient(user);
  };

  // Measure panel heights for VirtualList
  useEffect(() => {
    const updateHeights = () => {
      if (leftPanelRef.current)
        setLeftHeight(leftPanelRef.current.clientHeight);
      if (rightPanelRef.current)
        setRightHeight(rightPanelRef.current.clientHeight);
    };
    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, []);

  const UserRow = ({ index, style, data }) => {
    const { users, showAddButton, showRemoveButton } = data;
    const user = users[index];
    return (
      <div style={style}>
        <UserListItem
          user={user}
          showAddButton={showAddButton}
          showRemoveButton={showRemoveButton}
        />
      </div>
    );
  };

  const UserListItem = ({ user, showAddButton, showRemoveButton }) => {
    const isStagedUser = stagedUsers.some(
      (stagedUser) => stagedUser.id === user.id
    );
    const isStagedForRemoval = stagedForRemoval.some(
      (stagedUser) => stagedUser.id === user.id
    );
    const isExistingRecipient =
      members.some((member) => member.id === user.id) && !isStagedForRemoval;

    return (
      <ListItem
        disableGutters
        sx={{
          pr: 8, // space for right-side icons (tune as needed)
          pl: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* LEFT SIDE — NAME */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              textDecoration: isStagedForRemoval ? "line-through" : "none",
              color: isStagedForRemoval ? "error.dark" : "inherit",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              fontSize: {
                xs: "0.66rem",
                sm: "0.8rem",
                md: "0.875rem",
              },
            }}
          >
            {isXS ? TextSlicer(user.name, 14) : user.name}
          </Typography>
        </Box>

        {/* RIGHT SIDE — ACTION ICONS */}
        <Box
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 0.2, // perfect spacing
          }}
        >
          {/* STAR ICON */}
          {user?.members && (
            <Tooltip title="This can't be deleted from here because it has membership. You can delete it from Notifications tab > group details">
              <span>
                <IconButton
                  size="small"
                  disabled
                  sx={{
                    backgroundColor: "warning.main",
                    color: "white",
                    "&:disabled": {
                      backgroundColor: "warning.main",
                      opacity: 0.9,
                    },
                  }}
                >
                  <StarIcon fontSize="5px" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* ADD BUTTON */}
          {showAddButton && (
            <IconButton
              size="small"
              onClick={() => handleAddFromStaged(user)}
              disabled={addingUsers.has(user.id)}
              sx={{
                backgroundColor: isStagedForRemoval
                  ? "primary.main"
                  : "success.main",
                color: "white",
                "&:hover": {
                  backgroundColor: isStagedForRemoval
                    ? "primary.dark"
                    : "success.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
            >
              {addingUsers.has(user.id) ? (
                <CircularProgress size={16} />
              ) : (
                <AddIcon fontSize="small" />
              )}
            </IconButton>
          )}

          {/* REMOVE BUTTON */}
          {showRemoveButton && (
            <IconButton
              size="small"
              onClick={() => handleRemoveRecipient(user)}
              disabled={removingUsers.has(user.id) || user?.members}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "error.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
            >
              {removingUsers.has(user.id) ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>
      </ListItem>
    );
  };

  // Show message if device is not available
  if (!device) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Device information is not available. Please select a device to manage
          recipients.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: {
            xs: "60vh",
            sm: "100%",
            md: "100%",
          },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Add recipient{" "}
            {(stagedUsers.length > 0 || stagedForRemoval.length > 0) &&
              `(${stagedUsers.length} to add, ${stagedForRemoval.length} to remove)`}
          </Typography>

          {/* Warning Message */}
          <Typography
            variant="body2"
            color="error"
            sx={{
              mb: 1,
              fontStyle: "italic",
              fontSize: {
                xs: "0.62rem",
                sm: "0.8rem",
                md: "0.875rem",
              },
            }}
          >
            Selected Recipients will receive notifications from following units
          </Typography>

          {/* Staged changes info */}
          {(stagedUsers.length > 0 || stagedForRemoval.length > 0) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {stagedUsers.length > 0 &&
                `${stagedUsers.length} user(s) to be added`}
              {stagedUsers.length > 0 && stagedForRemoval.length > 0 && ", "}
              {stagedForRemoval.length > 0 &&
                `${stagedForRemoval.length} user(s) to be removed`}
              . Click "Save Changes" to apply.
            </Alert>
          )}
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            <CircularProgress color="#60a5fa" size={60} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", flex: 1, gap: 2, minHeight: 0 }}>
              {/* Left Panel - Available Users */}
              <Box sx={{ width: "50%" }} ref={leftPanelRef}>
                <Paper
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      p: { xs: 0.5, sm: 1, md: 1.5 },
                      bgcolor: "grey.300",
                      color: "black",
                      display: "flex",
                      alignItems: "center",
                      minHeight: { xs: 51, sm: 48 },
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      AVAILABLE USERS
                    </Typography>
                  </Box>

                  {/* Search */}
                  <Box sx={{ p: 1.5 }}>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={availableSearchQuery}
                      onChange={(e) => setAvailableSearchQuery(e.target.value)}
                    />
                  </Box>

                  {/* Users List */}
                  {leftHeight > 0 && (
                    <>
                      {filteredAvailableUsers.length > 0 ? (
                        <VirtualList
                          height={leftHeight}
                          width="100%"
                          itemCount={filteredAvailableUsers.length}
                          itemSize={itemHeight}
                          itemData={{
                            users: filteredAvailableUsers,
                            showAddButton: true,
                            showRemoveButton: false,
                          }}
                        >
                          {UserRow}
                        </VirtualList>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ py: 4 }}
                        >
                          No available users found
                        </Typography>
                      )}
                    </>
                  )}
                </Paper>
              </Box>

              {/* Right Panel - Selected Recipients */}
              <Box sx={{ width: "50%" }} ref={rightPanelRef}>
                <Paper
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      p: { xs: 0.5, sm: 1, md: 1.5 },
                      bgcolor: "grey.300",
                      color: "black",
                      display: "flex",
                      alignItems: "center",
                      minHeight: { xs: 51, sm: 48 },
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      SELECTED RECIPIENTS
                    </Typography>
                  </Box>

                  {/* Search */}
                  <Box sx={{ p: 1.5 }}>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={selectedSearchQuery}
                      onChange={(e) => setSelectedSearchQuery(e.target.value)}
                    />
                  </Box>

                  {/* Recipients List */}
                  {rightHeight > 0 && (
                    <>
                      {filteredSelectedRecipients.length > 0 ? (
                        <VirtualList
                          height={rightHeight}
                          width="100%"
                          itemCount={filteredSelectedRecipients.length}
                          itemSize={itemHeight}
                          itemData={{
                            users: filteredSelectedRecipients,
                            showAddButton: false,
                            showRemoveButton: true,
                          }}
                        >
                          {UserRow}
                        </VirtualList>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ py: 4 }}
                        >
                          No recipients selected
                        </Typography>
                      )}
                    </>
                  )}
                </Paper>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={
                  (stagedUsers.length === 0 && stagedForRemoval.length === 0) ||
                  savingRecipients
                }
                color="primary"
              >
                {savingRecipients ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="#60a5fa"
                      sx={{ mr: 1 }}
                    />
                    Saving...
                  </>
                ) : (
                  `Save Changes ${
                    stagedUsers.length > 0 || stagedForRemoval.length > 0
                      ? `(+${stagedUsers.length}, -${stagedForRemoval.length})`
                      : ""
                  }`
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
